import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import titleize from '@vangst/lib/titleize'
import useCompany, {
  CompanyComputed,
  optimisticDeleteCompany,
  prefetchCompany,
} from '@vangst/services/oogst/company/useCompany'
import { fetchPage } from '@vangst/services/oogst/page'
import { prefetchClientTrainings } from '@vangst/services/oogst/training/useTraining'
import { RobotsEnum } from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import React from 'react'
import Busy from '../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../components/navigation/Breadcrumbs'
import ClickyLink from '../../../../components/navigation/ClickyLink'
import Meta from '../../../../domains/application/Meta'
import CompanyBlockSummary from '../../../../domains/company/CompanyBlockSummary'
import CompanyRenderer from '../../../../domains/company/CompanyRenderer'
import CompanyTrainingsRenderer from '../../../../domains/company/CompanyTrainingsRenderer'
import CustomError from '../../../_error'

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

type StaticProps = GetStaticPropsContext<{ displayname?: string }>

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const displayname = params?.displayname ? params.displayname : undefined
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[displayname]', displayname || '')

  const vars = displayname
    ? { company: { displayname }, trainings: { displayname, first: 24 } }
    : undefined

  let company
  if (vars) {
    company = await prefetchCompany(queryClient, vars.company)
    await prefetchClientTrainings(queryClient, vars.trainings)
  }
  const is403 = company != null && company?.is403
  if (is403 && vars) optimisticDeleteCompany(queryClient, vars?.company)

  const co = company?.getClient as CompanyComputed
  const page = await fetchPage(queryClient, { rawUrl })
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    {
      href: Routes.COMPANIES,
      text: 'Companies',
    },
    {
      href: Routes.COMPANIES_DETAIL.replace(
        ':displayname',
        displayname ?? '',
      ) as Routes,
      text: titleize(displayname || 'Company'),
    },
    {
      href: null,
      text: 'Trainings',
    },
  ]

  return {
    props: {
      companyRoute: co?.routes?.detail ?? '',
      crumbs,
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      displayname,
      is403,
      page: page?.getPage ? page.getPage : null,
      vars,
    },
    notFound: company?.getClient == null && is403 === false,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

function BusyBusy() {
  return <Busy className="mt-12 flex items-center justify-center" />
}

const ThePage = React.memo(function ThePage(props: PropsType) {
  const { companyRoute, crumbs, displayname, page, vars } = props
  if (!displayname || !vars) return <BusyBusy />

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page?.metaInfo.canonicalUrl}
        crumbs={crumbs}
        description={page?.metaInfo.description}
        robots={page?.metaInfo.robots ?? RobotsEnum.IndexFollow}
        title={
          page?.metaInfo.title ?? page?.title ?? `${displayname} Trainings`
        }
      />
      <Breadcrumbs crumbs={crumbs} />
      <div className="contain-c template-aside-content with-mobile-aside-first mood-white">
        <article className="contain-xy">
          <CompanyBlockSummary at="trainings" variables={vars.company} />
          <CompanyTrainingsRenderer
            as="card"
            at="infinite"
            className="mt-4"
            classNameList="grid-three-up"
            variables={vars.company}
          />
        </article>
        <aside className="contain-xy">
          <header>
            <CompanyRenderer as="header" variables={vars.company} />
            <ClickyLink
              as="icon-text"
              className="motion-west link-orange mt-4 underline"
              href={companyRoute}
            >
              Back To Company Overview
            </ClickyLink>
          </header>
        </aside>
      </div>
    </>
  )
})

function CompanyMight403(props: PropsType) {
  const { authenticated, authenticating, viewer } = useViewer()
  const vars = props.vars?.company ? props.vars.company : { displayname: '' }
  const { data, isLoading } = useCompany(vars, {
    enabled: !!(viewer != null && props.vars?.company),
  })
  const unauthorized =
    !authenticated || (isLoading === false && data?.getClient == null)

  if (authenticating || isLoading) return <BusyBusy />
  if (unauthorized) return <CustomError statusCode={403} />
  return <ThePage {...props} />
}

export default function CompaniesDetailPage(props: PropsType) {
  if (!props.displayname || !props.vars) return <BusyBusy />
  if (props.is403 === true) return <CompanyMight403 {...props} />
  return <ThePage {...props} />
}
